/**
 *
 * ModalShipmentInformationDetected
 *
 */

import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { DhlText, DhlButton } from "@dhl-official/react-library";
import { Variants } from "@dhl-official/stencil-library";
import { injectIntl } from "react-intl";
import styled from "styled-components";
import { breakpoints } from "root/libs/ui-styleguide/src";
import { trackContentInteraction } from "root/libs/core-libs/src";
import { SEGMENT_TYPES } from "one-time-shipment/src/containers/SpotShipmentPage/constants";
import {
  UTF_EVENT_CONTENT_NAMES,
  UTF_EVENT_TYPES,
  UTF_EVENT_INTERACTIONS,
  UTF_EVENT_CONTENT_POSITION,
} from "root/libs/constants/src";
import { ContentModal, Headline } from "root/libs/ui-components/src";

const CopyText = styled(DhlText)`
  & > *:is(p, span) {
    margin-bottom: 20px;
    margin-top: 10px;
  }
`;

const CTAWrapper = styled.div`
  dhl-button {
    &:first-child {
      margin-bottom: 10px;
    }

    > button {
      width: 100%;
    }
  }

  @media screen and (min-width: ${breakpoints.tablet}) {
    dhl-button {
      &:first-child {
        margin-right: 20px;
      }

      > button {
        width: initial;
      }
    }
  }
`;

const Wrapper = styled.div``;

const COPY_ID_PREFIX = "ModalShipmentInformationDetected";

const ModalShipmentInformationDetected = (props) => {
  const { intl, trackEvent, onRestoreHandler, onClose, isBusiness } = props;

  const handleUtfClick = (attr) => {
    trackContentInteraction({
      name: UTF_EVENT_CONTENT_NAMES.RESTORE_SHIPMENT,
      type: UTF_EVENT_TYPES.MODAL,
      interaction: UTF_EVENT_INTERACTIONS.CLICK,
      position: UTF_EVENT_CONTENT_POSITION.PRODUCT_LANE,
      context: isBusiness ? SEGMENT_TYPES.business : SEGMENT_TYPES.private,
      attributes: attr,
    });
  };

  useEffect(() => {
    trackEvent({ shipmentInformationDetectedModalTriggered: true });
    trackContentInteraction({
      name: UTF_EVENT_CONTENT_NAMES.RESTORE_SHIPMENT,
      type: UTF_EVENT_TYPES.MODAL,
      interaction: UTF_EVENT_INTERACTIONS.LOAD,
      position: UTF_EVENT_CONTENT_POSITION.PRODUCT_LANE,
      context: isBusiness ? SEGMENT_TYPES.business : SEGMENT_TYPES.private,
    });
  }, []);

  const onAcceptedClick = () => {
    trackEvent({ clickedOnRestoreShipmentButton: true });
    handleUtfClick({ restore_shipment: "yes" });
    onRestoreHandler();
  };

  const onCanceledClick = () => {
    trackEvent({ clickedOnNewShipmentButton: true });
    handleUtfClick({ restore_shipment: "no" });
    onClose();
  };

  const getCopy = (id, values, prefix = COPY_ID_PREFIX) => {
    return intl.formatMessage({ id: `${prefix}.${id}` }, values);
  };

  return (
    <ContentModal
      {...props}
      smallCenteredModal
      shouldShowCloseButton={false}
      closeButtonAriaLabel={intl.formatMessage({ id: "SS.ContentModal.close" })}
      contentAriaLabel={getCopy("description")}
    >
      <Wrapper data-testid="shipment-info-detected-modal">
        <Headline priority={2}>{getCopy("headline")}</Headline>
        <CopyText isParagraph>{getCopy("description")}</CopyText>
        <CTAWrapper>
          <DhlButton
            type="button"
            clickEvent={onAcceptedClick}
            dataTestid="shipment-info-detected-modal-restore-btn"
          >
            {getCopy("primaryButton")}
          </DhlButton>
          <DhlButton
            type="button"
            variant={Variants.outline}
            clickEvent={onCanceledClick}
            dataTestid="shipment-info-detected-modal-new-btn"
          >
            {getCopy("secondaryButton")}
          </DhlButton>
        </CTAWrapper>
      </Wrapper>
    </ContentModal>
  );
};

ModalShipmentInformationDetected.propTypes = {
  intl: PropTypes.any.isRequired,
  trackEvent: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  isBusiness: PropTypes.bool,
  onRestoreHandler: PropTypes.func,
};

ModalShipmentInformationDetected.defaultProps = {
  isOpen: false,
};

export default injectIntl(ModalShipmentInformationDetected);
